import { create } from 'zustand';
import { readSessionReturn, sessionReturnPath, writeSessionReturn } from './session-return.js';
import type { SessionReturn } from './session-return.js';
import type { ALERT_VARIANT } from '#~/analytics/domains/alert-lifecycle.js';

export type RegistryList = typeof ALERT_VARIANT.STUDIOS | typeof ALERT_VARIANT.ORGANIZATIONS;
export interface RegistryListState {
  failed: boolean;
  retrying: boolean;
  eligible: boolean;
  retry: () => Promise<boolean>;
}

export interface AuthState {
  setUserInfo: (userInfo: any) => void;
  userInfo: any;
  registryLists: Partial<Record<RegistryList, RegistryListState>>;
  setRegistryList: (list: RegistryList, state: RegistryListState) => void;
  clearRegistryLists: () => void;
  resolveRegistryList: (list: RegistryList) => void;
  sessionEnded: SessionReturn | undefined;
  sessionEndedEnabled: boolean;
}

export const useAuthStore = create<AuthState>()((set) => ({
  setUserInfo: (userInfo) => set({ userInfo }),
  userInfo: null,
  registryLists: {},
  setRegistryList: (list, state) =>
    set((current) => ({
      registryLists: { ...current.registryLists, [list]: state }
    })),
  clearRegistryLists: () => set({ registryLists: {} }),
  resolveRegistryList: (list) =>
    set((current) => {
      const state = current.registryLists[list];
      return state
        ? { registryLists: { ...current.registryLists, [list]: { ...state, failed: false } } }
        : current;
    }),
  sessionEnded: readSessionReturn(),
  sessionEndedEnabled: false
}));

export const authStore = useAuthStore;

/** Sample the switch at failure time: unresolved/off failures are never queued. */
export const beginSessionEnded = (userId: string, deferToReload = false): boolean => {
  if (authStore.getState().sessionEnded) return true;
  if (!authStore.getState().sessionEndedEnabled) return false;
  const returnTo =
    sessionReturnPath(
      `${window.location.pathname}${window.location.search}${window.location.hash}`
    ) ?? '/';
  const sessionEnded: SessionReturn = { returnTo, userId, phase: 'prompt' };
  writeSessionReturn(sessionEnded);
  // Desktop is about to retire this document. Mount its prompt only after
  // reload, rather than running dialog/boot IPC effects during teardown.
  if (!deferToReload) authStore.setState({ sessionEnded });
  return true;
};
